// Build ids (docs/CONTRACTS.md sections 6 and 15; plan section E, "Data on the device"). The web build carries the id
// of the commit it was built from (vite.config.ts defines __NW_BUILD__, the same sha `deploy/push.sh` hands to the box
// as NW_BUILD), and every API answer carries the id of the server's release in `X-NW-Build`. 'dev' means "not a
// release" on either side, so a development server or a build outside git is never older or newer than anything.
// No DOM and no import.meta here: node:test runs this file (apps/web/tests/build.test.ts).
declare const __NW_BUILD__: string | undefined

export const UNKNOWN_BUILD = 'dev'

/** The id this bundle was built from. */
export const clientBuild: string = typeof __NW_BUILD__ === 'string' && __NW_BUILD__ !== '' ? __NW_BUILD__ : UNKNOWN_BUILD

/** True when both ids name a release and they differ: this client is older than the server (or the server was rolled back). */
export function buildDiffers(client: string, server: string | null): server is string {
  return !!server && server !== UNKNOWN_BUILD && client !== UNKNOWN_BUILD && server !== client
}

export interface BuildEvent {
  /** the server's release id */
  build: string
  /** true when an answer from that release could not be read by this client (a contract this client does not know) */
  incompatible: boolean
}

/**
 * Compares every API answer's `X-NW-Build` with the client's own id. A listener hears each server release once as
 * "newer" (`incompatible: false`: the app should look for the new version and offer it), and once more as
 * incompatible if an answer of that release was unreadable (`note(res, false)`: the app must reload).
 */
export function createBuildWatcher(client: string) {
  const listeners = new Set<(e: BuildEvent) => void>()
  const heard = new Set<string>()
  const emit = (build: string, incompatible: boolean) => {
    const key = `${incompatible ? 'incompatible' : 'newer'} ${build}`
    if (heard.has(key)) return
    heard.add(key)
    listeners.forEach((l) => l({ build, incompatible }))
  }
  return {
    /** Reads the build id of an API answer; `readable` is false when its body is not what the contract promises. */
    note(res: { headers: Pick<Headers, 'get'> }, readable = true): void {
      const server = res.headers.get('x-nw-build')
      if (!buildDiffers(client, server)) return
      emit(server, false)
      if (!readable) emit(server, true)
    },
    watch(listener: (e: BuildEvent) => void): () => void {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

const watcher = createBuildWatcher(clientBuild)
export const noteBuild = watcher.note
export const watchBuild = watcher.watch
